// POS Dashboard - Refactored
import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { Scissors, Package, CalendarDays, Gift, Wallet, Boxes, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Import POS components
import {
  POSHeader,
  ItemButton,
  TicketPanel,
  AgendaTab,
  CourtesyTab,
  InventoryTab,
  BarberDialog,
  CombinedPaymentDialog,
  PrintReceiptDialog,
  CashRegisterDialog,
  TipDialog,
  CatalogGrid,
  API,
} from '@/components/pos';
import AttendanceCodeGenerator from '@/components/pos/AttendanceCodeGenerator';

export default function POSDashboard() {
  const { user, logout, getAuthHeaders } = useAuth();
  
  // Data state
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [supplies, setSupplies] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [todayAppointments, setTodayAppointments] = useState([]);
  
  // Search state
  const [serviceSearch, setServiceSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');
  
  // Cart state
  const [cart, setCart] = useState([]);
  const [courtesyCart, setCourtesyCart] = useState([]);
  
  // Selection state
  const [selectedPayment, setSelectedPayment] = useState('');
  const [selectedBarber, setSelectedBarber] = useState('');
  const [selectedBranch, setSelectedBranch] = useState(user?.branch_id || '');
  
  // UI state
  const [isProcessing, setIsProcessing] = useState(false);
  const [ticketPanelOpen, setTicketPanelOpen] = useState(true);
  const [showBarberDialog, setShowBarberDialog] = useState(false);
  const [showCombinedPayment, setShowCombinedPayment] = useState(false);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [showCashRegister, setShowCashRegister] = useState(false);
  const [showTipDialog, setShowTipDialog] = useState(false);
  const [showAttendanceCode, setShowAttendanceCode] = useState(false);
  const [cashRegisterOpen, setCashRegisterOpen] = useState(false);
  const [lastSale, setLastSale] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [activeTab, setActiveTab] = useState('services');
  
  // Combined payment state
  const [combinedAmounts, setCombinedAmounts] = useState({ cash: 0, card: 0 });
  
  // Tip state
  const [tipAmount, setTipAmount] = useState(0);
  const [cardType, setCardType] = useState('TDD');
  
  // Loyalty state
  const [loyaltyCode, setLoyaltyCode] = useState('');
  const [loyaltyLoading, setLoyaltyLoading] = useState(false);
  const [lastCheckin, setLastCheckin] = useState(null);
  const [loyaltyRewards, setLoyaltyRewards] = useState([]);
  const [selectedReward, setSelectedReward] = useState(null);
  const [discountAmount, setDiscountAmount] = useState(0);

  // Check cash register status
  const checkCashRegisterStatus = async () => {
    if (!selectedBranch) return;
    try {
      const response = await axios.get(
        `${API}/cash-register/status/${selectedBranch}`,
        getAuthHeaders()
      );
      setCashRegisterOpen(response.data.can_sell);
    } catch (error) {
      setCashRegisterOpen(false);
    }
  };

  // Clock timer
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Check cash register on branch change
  useEffect(() => {
    if (selectedBranch) {
      checkCashRegisterStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch]);

  // Initial data fetch
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchInitialData(); }, []);
  
  // Set branch from user
  useEffect(() => {
    if (user?.branch_id) setSelectedBranch(user.branch_id);
  }, [user]);

  // Fetch barbers/appointments AND branch prices when branch changes
  useEffect(() => {
    if (selectedBranch) {
      fetchBarbers();
      fetchTodayAppointments();
      fetchBranchPrices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch]);

  // Data fetching functions
  const fetchInitialData = async () => {
    try {
      const [branchesRes, suppliesRes, rewardsRes] = await Promise.all([
        axios.get(`${API}/branches`, getAuthHeaders()),
        axios.get(`${API}/supplies`, getAuthHeaders()),
        axios.get(`${API}/loyalty/rewards?active_only=true`),
      ]);
      
      // Filter branches based on user role
      // Manager and Admin (encargado) can only see their assigned branch
      // Recepcionista sees only her assigned branches (branch_ids)
      // Master admin can see all branches
      let availableBranches = branchesRes.data;
      if ((user?.role === 'manager' || user?.role === 'admin') && user?.branch_id) {
        availableBranches = branchesRes.data.filter(b => b.id === user.branch_id);
      } else if (user?.role === 'recepcionista' && user?.branch_ids?.length > 0) {
        const allowed = new Set(user.branch_ids);
        availableBranches = branchesRes.data.filter(b => allowed.has(b.id));
      }
      
      setBranches(availableBranches);
      setSupplies(suppliesRes.data);
      setLoyaltyRewards(rewardsRes.data || []);
      
      // Set selected branch
      if (!selectedBranch && availableBranches.length > 0) {
        const initialBranch = user?.branch_id || availableBranches[0].id;
        setSelectedBranch(initialBranch);
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    }
  };

  // Fetch services and products with branch-specific prices
  const fetchBranchPrices = async () => {
    if (!selectedBranch) return;
    
    try {
      const [servicesRes, productsRes] = await Promise.all([
        axios.get(`${API}/branch-prices/by-branch/${selectedBranch}?item_type=service`, getAuthHeaders()),
        axios.get(`${API}/branch-prices/by-branch/${selectedBranch}?item_type=product`, getAuthHeaders()),
      ]);
      
      // Map items to use effective_price (branch price or base price)
      const servicesWithPrices = servicesRes.data.items.map(s => ({
        ...s,
        price: s.effective_price, // Use the effective price (branch or base)
        originalPrice: s.price,    // Keep original base price for reference
      }));
      
      const productsWithPrices = productsRes.data.items.map(p => ({
        ...p,
        price: p.effective_price,
        originalPrice: p.price,
      }));
      
      setServices(servicesWithPrices);
      setProducts(productsWithPrices);
    } catch (error) {
      // Fallback to base prices if branch prices endpoint fails
      // fallback to base prices
      try {
        const [servicesRes, productsRes] = await Promise.all([
          axios.get(`${API}/services?branch_id=${selectedBranch}`, getAuthHeaders()),
          axios.get(`${API}/products`, getAuthHeaders()),
        ]);
        setServices(servicesRes.data);
        setProducts(productsRes.data);
      } catch (fallbackError) {
        toast.error('Error al cargar precios');
      }
    }
  };

  const fetchBarbers = async () => {
    try {
      const response = await axios.get(`${API}/barbers?branch_id=${selectedBranch}`, getAuthHeaders());
      setBarbers(response.data);
    } catch (error) {
      // error handled
    }
  };

  const fetchTodayAppointments = async () => {
    try {
      const response = await axios.get(`${API}/appointments/today?branch_id=${selectedBranch}`, getAuthHeaders());
      setTodayAppointments(response.data);
    } catch (error) {
      // error handled
    }
  };

  // Cart functions
  const addToCart = (item, type) => {
    // Always add as new item to allow different barbers for same service
    setCart([...cart, {
      item_id: item.id,
      item_type: type,
      name: item.name,
      price: item.price,
      quantity: 1,
      barber_id: null,
      barber_name: null,
    }]);
    toast.success(`${item.name} agregado`);
  };

  const updateQuantity = (index, delta) => {
    const newCart = [...cart];
    newCart[index].quantity += delta;
    if (newCart[index].quantity <= 0) newCart.splice(index, 1);
    setCart(newCart);
  };

  const removeFromCart = (index) => {
    const newCart = [...cart];
    newCart.splice(index, 1);
    setCart(newCart);
  };

  const updateItemBarber = (index, barberId) => {
    const newCart = [...cart];
    newCart[index].barber_id = barberId;
    // Switching to single-barber clears any shared distribution
    newCart[index].barber_distribution = null;
    newCart[index].commission_pool_percentage = null;
    // Also store barber name for display/printing
    if (barberId) {
      const barber = barbers.find(b => b.id === barberId);
      newCart[index].barber_name = barber?.name || null;
    } else {
      newCart[index].barber_name = null;
    }
    setCart(newCart);
  };

  const updateItemDistribution = (index, distribution, poolPct) => {
    const newCart = [...cart];
    if (!distribution || distribution.length < 2) {
      // Clearing the shared distribution
      newCart[index].barber_distribution = null;
      newCart[index].commission_pool_percentage = null;
    } else {
      newCart[index].barber_distribution = distribution;
      newCart[index].commission_pool_percentage = poolPct;
      // Set primary barber_id to the first in distribution (for legacy display)
      newCart[index].barber_id = distribution[0].barber_id;
      const firstBarber = barbers.find(b => b.id === distribution[0].barber_id);
      newCart[index].barber_name = firstBarber?.name || null;
    }
    setCart(newCart);
  };

  const clearCart = () => {
    setCart([]);
    setSelectedBarber('');
    setSelectedPayment('');
    setSelectedReward(null);
    setDiscountAmount(0);
    setLastCheckin(null);
  };

  const getSubtotal = () => cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const getTotal = () => Math.max(0, getSubtotal() - discountAmount);
  const hasServices = () => cart.some(item => item.item_type === 'service');
  
  // Check if any service/product needs a barber
  const hasItemsWithoutBarber = () => cart.some(item => !item.barber_id);

  // Payment functions
  const handlePayClick = () => {
    if (cart.length === 0) return toast.error('Agrega productos al ticket');
    if (!selectedBranch) return toast.error('Selecciona una sucursal');
    if (!cashRegisterOpen) {
      toast.error('La caja no está abierta. Abre la caja antes de realizar ventas.');
      setShowCashRegister(true);
      return;
    }
    
    // Validate all items have a barber assigned
    const itemsWithoutBarber = cart.filter(item => !item.barber_id);
    if (itemsWithoutBarber.length > 0) {
      const itemNames = itemsWithoutBarber.map(item => `"${item.name}"`).join(', ');
      toast.error(`Selecciona un barbero para: ${itemNames}`, {
        duration: 5000,
      });
      return;
    }

    // Validate payment method is selected
    if (!selectedPayment) {
      toast.error('Seleccione forma de pago');
      return;
    }
    
    // If paying with card or transfer, show tip dialog
    if (selectedPayment === 'card' || selectedPayment === 'transfer') {
      setShowTipDialog(true);
      return;
    }
    
    processSale();
  };

  const handleTipConfirm = (tip, selectedCardType) => {
    setTipAmount(tip);
    setCardType(selectedCardType);
    setShowTipDialog(false);
    processSale(tip, selectedCardType);
  };

  const handleCombinedPayment = () => {
    if (cart.length === 0) return toast.error('Agrega productos al ticket');
    if (!selectedBranch) return toast.error('Selecciona una sucursal');
    
    // Validate all items have a barber assigned
    const itemsWithoutBarber = cart.filter(item => !item.barber_id);
    if (itemsWithoutBarber.length > 0) {
      const itemNames = itemsWithoutBarber.map(item => `"${item.name}"`).join(', ');
      toast.error(`Selecciona un barbero para: ${itemNames}`, {
        duration: 5000,
      });
      return;
    }
    
    setShowCombinedPayment(true);
  };

  const handleCombinedConfirm = (cashAmount, cardAmount) => {
    setCombinedAmounts({ cash: cashAmount, card: cardAmount });
    setSelectedPayment('combined');
    setShowCombinedPayment(false);
    
    // Process sale with combined payment
    processSaleWithAmounts(cashAmount, cardAmount);
  };

  const processSale = async (tip = 0, tipCardType = 'TDD') => {
    setIsProcessing(true);
    try {
      // Prepare items with their barber_id and optional barber_distribution
      const itemsWithBarbers = cart.map(item => ({
        ...item,
        barber_id: item.barber_id || null,
        barber_distribution: item.barber_distribution || null,
        commission_pool_percentage: item.commission_pool_percentage || null,
      }));
      
      const payload = {
        items: itemsWithBarbers,
        payment_method: selectedPayment,
        total: getTotal(),
        subtotal: getSubtotal(),
        branch_id: selectedBranch,
        tip_amount: tip > 0 ? tip : null,
        card_type: tip > 0 ? tipCardType : null,
        // Discount info
        discount_amount: discountAmount > 0 ? discountAmount : null,
        loyalty_user_code: lastCheckin?.code || null,
        reward_id: selectedReward?.id || null,
        reward_name: selectedReward?.name || null,
        reward_points: selectedReward?.points_required || null,
      };
      
      // Add combined amounts if applicable
      if (selectedPayment === 'combined') {
        payload.cash_amount = combinedAmounts.cash;
        payload.card_amount = combinedAmounts.card;
      }
      
      const response = await axios.post(`${API}/sales`, payload, getAuthHeaders());
      
      const tipMessage = tip > 0 ? ` (Propina: $${tip.toFixed(2)})` : '';
      const discountMessage = discountAmount > 0 ? ` (Descuento: $${discountAmount.toFixed(2)})` : '';
      toast.success(`Venta registrada exitosamente${tipMessage}${discountMessage}`);
      
      // Get barber names for receipt
      const barberNames = [...new Set(cart.filter(i => i.barber_name).map(i => i.barber_name))];
      const branch = branches.find(b => b.id === selectedBranch);
      setLastSale({
        id: response.data.id,
        items: cart,
        total: getTotal(),
        subtotal: getSubtotal(),
        discountAmount: discountAmount,
        rewardName: selectedReward?.name,
        tipAmount: tip,
        paymentMethod: selectedPayment,
        cashAmount: combinedAmounts.cash,
        cardAmount: combinedAmounts.card,
        barberName: barberNames.join(', ') || null,
        branchName: branch?.name,
        loyaltyPoints: lastCheckin?.points_added,
      });
      
      // Show print dialog
      setShowPrintDialog(true);
      
      // Reset cart and discount
      setCart([]);
      setSelectedBarber('');
      setShowBarberDialog(false);
      setSelectedPayment('');
      setCombinedAmounts({ cash: 0, card: 0 });
      setLastCheckin(null);
      setSelectedReward(null);
      setDiscountAmount(0);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar venta');
    } finally {
      setIsProcessing(false);
    }
  };

  const processSaleWithAmounts = async (cashAmount, cardAmount) => {
    setIsProcessing(true);
    try {
      // Prepare items with their barber_id and optional barber_distribution
      const itemsWithBarbers = cart.map(item => ({
        ...item,
        barber_id: item.barber_id || null,
        barber_distribution: item.barber_distribution || null,
        commission_pool_percentage: item.commission_pool_percentage || null,
      }));
      
      const response = await axios.post(`${API}/sales`, {
        items: itemsWithBarbers,
        payment_method: 'combined',
        total: getTotal(),
        subtotal: getSubtotal(),
        branch_id: selectedBranch,
        cash_amount: cashAmount,
        card_amount: cardAmount,
        // Discount info
        discount_amount: discountAmount > 0 ? discountAmount : null,
        loyalty_user_code: lastCheckin?.code || null,
        reward_id: selectedReward?.id || null,
        reward_name: selectedReward?.name || null,
        reward_points: selectedReward?.points_required || null,
      }, getAuthHeaders());
      
      const discountMessage = discountAmount > 0 ? ` (Descuento: $${discountAmount.toFixed(2)})` : '';
      toast.success(`Venta registrada: $${cashAmount.toFixed(2)} efectivo + $${cardAmount.toFixed(2)} tarjeta${discountMessage}`);
      
      // Get barber names for receipt
      const barberNames = [...new Set(cart.filter(i => i.barber_name).map(i => i.barber_name))];
      const branch = branches.find(b => b.id === selectedBranch);
      setLastSale({
        id: response.data.id,
        items: cart,
        total: getTotal(),
        subtotal: getSubtotal(),
        discountAmount: discountAmount,
        rewardName: selectedReward?.name,
        paymentMethod: 'combined',
        cashAmount: cashAmount,
        cardAmount: cardAmount,
        barberName: barberNames.join(', ') || null,
        branchName: branch?.name,
        loyaltyPoints: lastCheckin?.points_added,
      });
      
      // Show print dialog
      setShowPrintDialog(true);
      
      // Reset cart and discount
      setCart([]);
      setSelectedBarber('');
      setShowBarberDialog(false);
      setSelectedPayment('');
      setCombinedAmounts({ cash: 0, card: 0 });
      setLastCheckin(null);
      setSelectedReward(null);
      setDiscountAmount(0);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar venta');
    } finally {
      setIsProcessing(false);
    }
  };

  // Appointment functions
  const markAppointmentCompleted = async (appointmentId) => {
    try {
      await axios.put(`${API}/appointments/${appointmentId}/status?status=completed`, {}, getAuthHeaders());
      toast.success('Cita completada');
      fetchTodayAppointments();
    } catch (error) {
      toast.error('Error al actualizar cita');
    }
  };

  const confirmAppointment = async (appointmentId) => {
    try {
      await axios.put(`${API}/appointments/${appointmentId}/status?status=confirmed`, {}, getAuthHeaders());
      toast.success('Cita confirmada');
      fetchTodayAppointments();
    } catch (error) {
      toast.error('Error al confirmar cita');
    }
  };

  // Price update function (master_admin only)
  const handlePriceUpdate = async (itemId, itemType, newPrice) => {
    try {
      const item = itemType === 'service' 
        ? services.find(s => s.id === itemId)
        : products.find(p => p.id === itemId);
      
      if (!item) return;

      // Update branch-specific price
      await axios.post(`${API}/branch-prices`, {
        item_id: itemId,
        item_type: itemType,
        branch_id: selectedBranch,
        price: newPrice
      }, getAuthHeaders());

      // Update local state
      if (itemType === 'service') {
        setServices(services.map(s => s.id === itemId ? { ...s, price: newPrice, effective_price: newPrice, has_branch_price: true } : s));
      } else {
        setProducts(products.map(p => p.id === itemId ? { ...p, price: newPrice, effective_price: newPrice, has_branch_price: true } : p));
      }

      const branchName = branches.find(b => b.id === selectedBranch)?.name || 'esta sucursal';
      toast.success(`Precio actualizado a $${newPrice.toFixed(2)} para ${branchName}`);
    } catch (error) {
      toast.error('Error al actualizar precio');
    }
  };

  // Courtesy functions
  const addToCourtesy = (supply) => {
    const existingIndex = courtesyCart.findIndex(i => i.supply_id === supply.id);
    if (existingIndex >= 0) {
      const newCart = [...courtesyCart];
      newCart[existingIndex].quantity += 1;
      setCourtesyCart(newCart);
    } else {
      setCourtesyCart([...courtesyCart, {
        supply_id: supply.id,
        name: supply.name,
        unit: supply.unit,
        stock: supply.stock,
        quantity: 1,
      }]);
    }
    toast.success(`${supply.name} agregado a cortesías`);
  };

  const updateCourtesyQuantity = (index, delta) => {
    const newCart = [...courtesyCart];
    newCart[index].quantity += delta;
    if (newCart[index].quantity <= 0) newCart.splice(index, 1);
    setCourtesyCart(newCart);
  };

  const removeFromCourtesy = (index) => {
    const newCart = [...courtesyCart];
    newCart.splice(index, 1);
    setCourtesyCart(newCart);
  };

  const clearCourtesy = () => setCourtesyCart([]);

  const processCourtesy = async () => {
    if (courtesyCart.length === 0) return toast.error('Agrega insumos a la lista de cortesías');
    if (!selectedBranch) return toast.error('Selecciona una sucursal');

    setIsProcessing(true);
    try {
      await axios.post(`${API}/supplies/use-courtesy`, {
        items: courtesyCart.map(item => ({
          supply_id: item.supply_id,
          quantity: item.quantity,
        })),
        branch_id: selectedBranch,
      }, getAuthHeaders());
      toast.success('Cortesías registradas - Inventario actualizado');
      setCourtesyCart([]);
      const suppliesRes = await axios.get(`${API}/supplies`, getAuthHeaders());
      setSupplies(suppliesRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar cortesías');
    } finally {
      setIsProcessing(false);
    }
  };

  // Loyalty check-in
  const processLoyaltyCheckin = async () => {
    if (!loyaltyCode || loyaltyCode.length !== 3) {
      return toast.error('Ingresa un código válido de 3 caracteres');
    }
    if (!selectedBranch) return toast.error('Selecciona una sucursal');

    setLoyaltyLoading(true);
    try {
      const response = await axios.post(
        `${API}/loyalty/checkin?user_code=${loyaltyCode.toUpperCase()}&branch_id=${selectedBranch}`,
        {},
        getAuthHeaders()
      );
      setLastCheckin(response.data);
      setLoyaltyCode('');
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código no encontrado');
    } finally {
      setLoyaltyLoading(false);
    }
  };

  return (
    <div className="h-screen flex bg-[#050505]" data-testid="pos-dashboard">
      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        <POSHeader
          branches={branches}
          selectedBranch={selectedBranch}
          setSelectedBranch={setSelectedBranch}
          user={user}
          logout={logout}
          ticketPanelOpen={ticketPanelOpen}
          setTicketPanelOpen={setTicketPanelOpen}
          setShowCashRegister={setShowCashRegister}
          cashRegisterOpen={cashRegisterOpen}
          setShowAttendanceCode={setShowAttendanceCode}
        />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
          <div className="px-6 pt-4">
            <TabsList className="bg-zinc-900 border border-zinc-800">
              <TabsTrigger 
                value="services" 
                className="data-[state=active]:bg-gold data-[state=active]:text-black"
                data-testid="pos-tab-services"
              >
                <Scissors className="w-4 h-4 mr-2" />
                Servicios
              </TabsTrigger>
              <TabsTrigger 
                value="products"
                className="data-[state=active]:bg-gold data-[state=active]:text-black"
                data-testid="pos-tab-products"
              >
                <Package className="w-4 h-4 mr-2" />
                Productos
              </TabsTrigger>
              <TabsTrigger 
                value="agenda"
                className="data-[state=active]:bg-gold data-[state=active]:text-black"
                data-testid="pos-tab-agenda"
              >
                <CalendarDays className="w-4 h-4 mr-2" />
                Agenda ({todayAppointments.length})
              </TabsTrigger>
              <TabsTrigger 
                value="cortesias"
                className="data-[state=active]:bg-emerald-500 data-[state=active]:text-black"
                data-testid="pos-tab-cortesias"
              >
                <Gift className="w-4 h-4 mr-2" />
                Cortesías {courtesyCart.length > 0 && `(${courtesyCart.length})`}
              </TabsTrigger>
              <TabsTrigger 
                value="inventario"
                className="data-[state=active]:bg-blue-500 data-[state=active]:text-white"
                data-testid="pos-tab-inventario"
              >
                <Boxes className="w-4 h-4 mr-2" />
                Inventario
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="services" className="flex-1 p-6 mt-0">
            <CatalogGrid
              items={services}
              type="service"
              search={serviceSearch}
              onSearchChange={setServiceSearch}
              onAdd={addToCart}
              isMasterAdmin={user?.role === 'master_admin'}
              onPriceUpdate={handlePriceUpdate}
              placeholder="Buscar servicio..."
              testIdSearch="service-search-input"
            />
          </TabsContent>

          <TabsContent value="products" className="flex-1 p-6 mt-0">
            <CatalogGrid
              items={products}
              type="product"
              search={productSearch}
              onSearchChange={setProductSearch}
              onAdd={addToCart}
              isMasterAdmin={user?.role === 'master_admin'}
              onPriceUpdate={handlePriceUpdate}
              placeholder="Buscar producto..."
              testIdSearch="product-search-input"
            />
          </TabsContent>

          <TabsContent value="agenda" className="flex-1 p-6 mt-0 overflow-auto">
            <AgendaTab
              todayAppointments={todayAppointments}
              onRefresh={fetchTodayAppointments}
              onMarkCompleted={markAppointmentCompleted}
              onConfirmAppointment={confirmAppointment}
              branchId={selectedBranch}
              barbers={barbers}
              services={services}
              getAuthHeaders={getAuthHeaders}
            />
          </TabsContent>

          <TabsContent value="cortesias" className="flex-1 p-6 mt-0 overflow-auto">
            <CourtesyTab
              supplies={supplies}
              courtesyCart={courtesyCart}
              addToCourtesy={addToCourtesy}
              updateCourtesyQuantity={updateCourtesyQuantity}
              removeFromCourtesy={removeFromCourtesy}
              clearCourtesy={clearCourtesy}
              processCourtesy={processCourtesy}
              isProcessing={isProcessing}
              branchId={selectedBranch}
              getAuthHeaders={getAuthHeaders}
              userRole={user?.role}
            />
          </TabsContent>

          <TabsContent value="inventario" className="flex-1 p-6 mt-0 overflow-auto">
            <InventoryTab
              branchId={selectedBranch}
              getAuthHeaders={getAuthHeaders}
              userRole={user?.role}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Ticket Panel */}
      <TicketPanel
        ticketPanelOpen={ticketPanelOpen}
        cart={cart}
        barbers={barbers}
        selectedPayment={selectedPayment}
        setSelectedPayment={setSelectedPayment}
        updateQuantity={updateQuantity}
        removeFromCart={removeFromCart}
        updateItemBarber={updateItemBarber}
        updateItemDistribution={updateItemDistribution}
        clearCart={clearCart}
        getSubtotal={getSubtotal}
        getTotal={getTotal}
        handlePayClick={handlePayClick}
        isProcessing={isProcessing}
        user={user}
        branches={branches}
        selectedBranch={selectedBranch}
        setSelectedBranch={setSelectedBranch}
        loyaltyCode={loyaltyCode}
        setLoyaltyCode={setLoyaltyCode}
        loyaltyLoading={loyaltyLoading}
        processLoyaltyCheckin={processLoyaltyCheckin}
        lastCheckin={lastCheckin}
        setLastCheckin={setLastCheckin}
        onCombinedPayment={handleCombinedPayment}
        loyaltyRewards={loyaltyRewards}
        selectedReward={selectedReward}
        setSelectedReward={setSelectedReward}
        discountAmount={discountAmount}
        setDiscountAmount={setDiscountAmount}
      />

      {/* Combined Payment Dialog */}
      <CombinedPaymentDialog
        isOpen={showCombinedPayment}
        onClose={() => setShowCombinedPayment(false)}
        total={getTotal()}
        onConfirm={handleCombinedConfirm}
      />

      {/* Dialogs */}
      <BarberDialog
        open={showBarberDialog}
        onOpenChange={setShowBarberDialog}
        barbers={barbers}
        selectedBarber={selectedBarber}
        setSelectedBarber={setSelectedBarber}
        onConfirm={processSale}
        onSkip={() => {
          setSelectedBarber('');
          processSale();
        }}
        isProcessing={isProcessing}
      />

      {/* Print Receipt Dialog */}
      <PrintReceiptDialog
        isOpen={showPrintDialog}
        onClose={() => setShowPrintDialog(false)}
        saleData={lastSale}
        businessName={branches.find(b => b.id === selectedBranch)?.business_name || 'THE HOLLAND BARBERSHOP'}
        branchName={branches.find(b => b.id === selectedBranch)?.name}
      />

      {/* Cash Register Dialog */}
      <CashRegisterDialog
        open={showCashRegister}
        onOpenChange={setShowCashRegister}
        branchId={selectedBranch}
        getAuthHeaders={getAuthHeaders}
        onStatusChange={(isOpen) => setCashRegisterOpen(isOpen)}
        userRole={user?.role}
      />

      {/* Tip Dialog */}
      <TipDialog
        isOpen={showTipDialog}
        onClose={() => setShowTipDialog(false)}
        subtotal={getTotal()}
        onConfirm={handleTipConfirm}
        paymentMethod={selectedPayment}
        branchClabe={branches.find(b => b.id === selectedBranch)?.clabe || ''}
        branchBanco={branches.find(b => b.id === selectedBranch)?.clabe_banco || ''}
        branchBeneficiario={branches.find(b => b.id === selectedBranch)?.clabe_beneficiario || ''}
      />

      {/* Attendance Code Generator */}
      <AttendanceCodeGenerator
        open={showAttendanceCode}
        onOpenChange={setShowAttendanceCode}
        getAuthHeaders={getAuthHeaders}
      />
    </div>
  );
}
